import OpenInNewIcon from "@mui/icons-material/OpenInNewRounded";
import { Button, Stack } from "@mui/material";
import { useState } from "react";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import { invalidResult } from "@entrilia/shared/utilities/validators";
import { api } from "../../../api/operationsApi";
import type { CompanyWithOrganization } from "../../common/CompanySelector";
import CompanySelector from "../../common/CompanySelector";
import LoadingFailedAlert from "../../common/LoadingFailedAlert";
import PageTitle from "../../common/PageTitle";
import { getFundOpsUrl } from "./fundOpsLink";

const OpenFundOpsContent = () => {
  const [selectedCompany, setSelectedCompany] = useState<CompanyWithOrganization | null>(null);

  const [organizations, fetchError] = useFetch(api.clientProvisioning.getExistingOrganizations);

  if (fetchError) {
    logError(fetchError, "[OpenFundOpsContent] getExistingOrganizations");
    return <LoadingFailedAlert apiError={fetchError} />;
  }

  if (organizations === undefined) {
    return <InlineLoader />;
  }

  const fundOpsUrl = selectedCompany ? getFundOpsUrl(selectedCompany) : undefined;
  const validationResult =
    selectedCompany && !fundOpsUrl
      ? invalidResult("This client's Business Central application has no tenant, environment or company name.")
      : undefined;

  return (
    <Stack spacing={3} sx={{ alignItems: "flex-start", width: "24rem" }}>
      <PageTitle
        title="Open Fund Operations"
        subtitle="🔓 Access a client's Business Central without a role"
        defaultExpanded
      >
        Opens the client&apos;s Business Central in a new tab using your delegated admin access. Business Central shows
        you as Entrilia Technician.
        <br />
        <br />
        You don&apos;t need a role on the client: this skips the access step the Admin Portal runs before opening Fund
        Operations, so no role or Business Central permission is granted to you.
        <br />
        <br />
        If the client&apos;s tenant has no delegated admin relationship, Microsoft will refuse the sign-in.
      </PageTitle>
      <CompanySelector
        organizations={organizations}
        value={selectedCompany}
        onChange={setSelectedCompany}
        validationResult={validationResult}
      />
      <Button
        component="a"
        variant="contained"
        href={fundOpsUrl}
        target="_blank"
        rel="noopener noreferrer"
        disabled={!fundOpsUrl}
        endIcon={<OpenInNewIcon />}
      >
        Open Fund Operations
      </Button>
    </Stack>
  );
};

export default OpenFundOpsContent;
